import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Wrench, Package, Receipt } from '@phosphor-icons/react';

/**
 * /partners/contractor — public landing page for irrigation contractors.
 *
 * The pitch is concrete: print this PDF, hand it to every new homeowner,
 * earn a referral commission for each who later signs up. The PDF is
 * generated server-side at /api/contractor-handoff/pdf with the contractor's
 * name and a kebab-case ref code baked in.
 *
 * No login required to grab a PDF — friction-free for cold visitors. The
 * referral attribution happens via the ?ref query param when homeowners
 * eventually land on getturfpulse.com after scanning the QR.
 */
export default function ContractorPartnerPage() {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const [name, setName] = useState('');
  const [ref, setRef] = useState('');
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  const slugify = (s) =>
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 40);

  const onNameChange = (e) => {
    setName(e.target.value);
    if (!ref || ref === slugify(name)) {
      setRef(slugify(e.target.value));
    }
  };

  const handleDownload = async () => {
    setError('');
    if (name.trim().length < 2) {
      setError('Enter your business name (2+ characters).');
      return;
    }
    const finalRef = (ref || slugify(name)).slice(0, 40);
    if (!/^[a-z0-9-]+$/.test(finalRef)) {
      setError('Ref code can only contain lowercase letters, numbers, and dashes.');
      return;
    }
    setGenerating(true);
    try {
      const url = `${apiUrl}/api/contractor-handoff/pdf?contractor=${encodeURIComponent(
        name.trim()
      )}&ref=${encodeURIComponent(finalRef)}`;
      window.open(url, '_blank');
    } catch (e) {
      setError('Could not generate PDF. Try again or contact support.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white"
      data-testid="contractor-partner-page"
    >
      <SEO
        title="Contractor Handoff Program — TurfPulse"
        description="Print a professional 1-page handoff guide for every Rachio install. Earn referral commissions when your homeowners upgrade. Free for irrigation contractors."
        canonical="https://getturfpulse.com/partners/contractor"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Partners', path: '/partners' },
          { label: 'Contractor Handoff', path: '/partners/contractor' },
        ]}
      />

      {/* Hero */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
            <Wrench size={14} weight="fill" /> CONTRACTOR HANDOFF PROGRAM
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-zinc-900 sm:text-5xl">
            Print this. Hand it to every homeowner. Get paid when they upgrade.
          </h1>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            You install hundreds of Rachio controllers. The homeowners take
            over, default-fire a calendar schedule, waste water, then call you
            for support. This 1-page printable handoff guide solves that —
            with your name on it, and a referral commission on every signup.
          </p>
          <p className="mt-3 text-base text-zinc-600">
            Free to use. No login. Generate the PDF, print a stack, leave one
            in every install.
          </p>
        </div>
      </section>

      {/* Generator */}
      <section className="mx-auto max-w-3xl px-6 py-12">
        <Card className="border-emerald-200">
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-2xl font-black text-zinc-900">
              Generate your handoff PDF
            </h2>
            <p className="mt-2 text-sm text-zinc-600">
              Takes 5 seconds. The PDF is personalized with your name and
              tracks every signup back to your referral code.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="contractor-name">Your business name</Label>
                <Input
                  id="contractor-name"
                  data-testid="contractor-name-input"
                  placeholder="Jess Harlor Irrigation"
                  value={name}
                  onChange={onNameChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ref-code">
                  Referral code{' '}
                  <span className="text-xs text-zinc-500">(auto-generated, editable)</span>
                </Label>
                <Input
                  id="ref-code"
                  data-testid="contractor-ref-input"
                  placeholder="jess-harlor"
                  value={ref}
                  onChange={(e) =>
                    setRef(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                  }
                  className="mt-1 font-mono"
                  maxLength={40}
                />
                <p className="mt-1 text-xs text-zinc-500">
                  This is the tracking code on every QR scan from your handoffs.
                  Lowercase letters, numbers, and dashes only.
                </p>
              </div>

              {error && (
                <p
                  className="text-sm font-semibold text-rose-700"
                  data-testid="contractor-form-error"
                >
                  {error}
                </p>
              )}

              <Button
                onClick={handleDownload}
                disabled={generating || name.trim().length < 2}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                data-testid="contractor-download-btn"
              >
                {generating
                  ? 'Generating…'
                  : 'Download my handoff PDF'}{' '}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              {ref && (
                <p
                  className="text-xs text-zinc-500"
                  data-testid="contractor-qr-target-preview"
                >
                  QR will point to:{' '}
                  <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-zinc-700">
                    getturfpulse.com/methodology/probe-driven-irrigation?ref={ref}
                  </code>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* What's on the PDF */}
      <section className="border-y border-zinc-200 bg-zinc-50/60">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <h2 className="text-2xl font-black text-zinc-900">
            What the homeowner gets
          </h2>
          <div
            className="mt-6 grid gap-4 sm:grid-cols-3"
            data-testid="contractor-pdf-features"
          >
            <Card>
              <CardContent className="p-5">
                <Package size={28} className="text-emerald-700" weight="duotone" />
                <h3 className="mt-2 font-bold text-zinc-900">Your name on it</h3>
                <p className="mt-1 text-sm text-zinc-700">
                  Header reads "Installed & configured by [Your Business]." Your
                  professional signature on every install.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <Wrench size={28} className="text-emerald-700" weight="duotone" />
                <h3 className="mt-2 font-bold text-zinc-900">3 first-30-days steps</h3>
                <p className="mt-1 text-sm text-zinc-700">
                  Map zones, run catch-cup test, bury soil probe. Reduces your
                  support calls — they fix the basics themselves.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <Receipt size={28} className="text-emerald-700" weight="duotone" />
                <h3 className="mt-2 font-bold text-zinc-900">QR code with your ref</h3>
                <p className="mt-1 text-sm text-zinc-700">
                  Scans to the open methodology page. Every homeowner who
                  signs up via your QR earns you a referral commission.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Commission math */}
      <section className="mx-auto max-w-4xl px-6 py-12">
        <h2 className="text-2xl font-black text-zinc-900">
          The commission math
        </h2>
        <p className="mt-3 text-zinc-700 leading-relaxed">
          Most contractors hand off 100–300 Rachios per year. A 5% conversion
          to TurfPulse Enthusiast ($15/mo) compounds:
        </p>
        <Card className="mt-5">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-zinc-200">
                {[
                  ['Installs per year', '200'],
                  ['Conversion rate (conservative)', '5%'],
                  ['New TurfPulse subs / year', '10'],
                  ['Your share of the recurring (20% lifetime)', '$3 / month / sub'],
                  ['Year 1 trailing earnings', '~$240'],
                  ['Year 2 (cumulative installs + retained subs)', '~$580'],
                  ['Year 3', '~$1,000+ recurring'],
                ].map(([k, v], i) => (
                  <tr key={i} className={i >= 5 ? 'bg-emerald-50/60 font-bold' : ''}>
                    <td className="px-4 py-2 text-zinc-700">{k}</td>
                    <td className="px-4 py-2 font-mono text-zinc-900 text-right">
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <p className="mt-3 text-xs text-zinc-500 italic">
          Commission program details finalized when you reach 5+ tracked
          signups. We'll reach out via the ref code on file.
        </p>
      </section>
    </div>
  );
}
